import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAppEnvironment,
  getFrontendBuildSha,
  getRecentClientEvents,
  getRecentRequestIds,
} from '../betaHelp';
import { getStoredUser, parseJsonSafe } from '../api/client';
import { feedbackKindForCategory, postSubmitAppFeedback } from '../api/tutor';
import { SUPPORT_EMAIL_ADDRESS, SUPPORT_EMAIL_GUIDANCE } from '../copy/onboardingStatus';

const CATEGORY_OPTIONS = [
  { value: 'bug', label: 'Bug' },
  { value: 'confusing', label: 'Confusing' },
  { value: 'idea', label: 'Idea' },
  { value: 'praise', label: 'Praise' },
];

function extractRouteContext(pathname) {
  const sessionMatch = String(pathname || '').match(/\/sessions\/([^/]+)/);
  return {
    session_id: sessionMatch?.[1] || null,
  };
}

function friendlyPageLabel(pathname) {
  const path = String(pathname || '').split('?')[0];
  if (path === '/dashboard') {
    return 'Dashboard';
  }
  if (path === '/practice') {
    return 'Practice';
  }
  if (path === '/beta-help') {
    return 'Beta Help';
  }
  if (path.includes('/sessions/') && path.endsWith('/tutor')) {
    return 'Tutor session';
  }
  if (path.includes('/sessions/') && path.endsWith('/summary')) {
    return 'Session summary';
  }
  if (path.includes('/sessions/') && path.includes('/exam')) {
    return 'Exam session';
  }
  if (path.startsWith('/admin/')) {
    return 'Admin page';
  }
  if (path.startsWith('/tutor/')) {
    return 'Tutor page';
  }
  if (path.startsWith('/parent/')) {
    return 'Parent page';
  }
  return 'Current page';
}

function buildSummary(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed;
}

function boundedContextValue(value, limit = 160) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return String(value).slice(0, limit);
}

function ReportIssueModal({ context = {}, onClose }) {
  const location = useLocation();
  const user = getStoredUser();
  const recentRequestIds = getRecentRequestIds();
  const latestRequestId = recentRequestIds[0]?.request_id || '';
  const timestamp = useMemo(() => new Date().toISOString(), []);
  const closeTimerRef = useRef(null);
  const routeContext = extractRouteContext(location.pathname);
  const [category, setCategory] = useState('bug');
  const [whatHappened, setWhatHappened] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const mergedContext = {
    ...routeContext,
    ...context,
  };
  const currentPage = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  const pageLabel = friendlyPageLabel(location.pathname);
  const canSubmit = whatHappened.trim().length > 0 && !isSubmitting;

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = whatHappened.trim();
    if (!trimmed) {
      setError('Describe what happened before sending.');
      return;
    }

    setIsSubmitting(true);
    setNotice('');
    setError('');
    try {
      const requestIds = getRecentRequestIds().map((entry) => ({
        request_id: boundedContextValue(entry.request_id),
        timestamp: boundedContextValue(entry.timestamp, 40),
      }));
      const clientEvents = getRecentClientEvents();
      const response = await postSubmitAppFeedback({
        summary: buildSummary(trimmed),
        raw_text: trimmed.slice(0, 2000),
        kind: feedbackKindForCategory(category),
        category,
        reporter_context: {
          role: user?.role || null,
          email: user?.email || null,
        },
        runtime_context: {
          source: 'global_report_issue',
          page: currentPage.slice(0, 500),
          app_environment: getAppEnvironment(),
          frontend_build_sha: getFrontendBuildSha(),
          timestamp,
          latest_request_id: latestRequestId || null,
          request_ids: requestIds,
          session_id: boundedContextValue(mergedContext.session_id),
          attempt_id: boundedContextValue(mergedContext.attempt_id),
          part_attempt_id: boundedContextValue(mergedContext.part_attempt_id),
          question_id: boundedContextValue(mergedContext.question_id),
          family_key: boundedContextValue(mergedContext.family_key),
          skill_code: boundedContextValue(mergedContext.skill_code),
          micro_skill_code: boundedContextValue(mergedContext.micro_skill_code),
          console_errors: clientEvents.console_errors,
          failed_fetches: clientEvents.failed_fetches,
          viewport: {
            width: Number(window.innerWidth) || 0,
            height: Number(window.innerHeight) || 0,
          },
          user_agent: String(navigator.userAgent || '').slice(0, 500),
        },
      });
      const data = (await parseJsonSafe(response)) || {};
      if (!response.ok) {
        throw new Error(data?.detail || data?.message || 'Could not send report.');
      }
      setNotice('Thanks — sent.');
      setWhatHappened('');
      closeTimerRef.current = setTimeout(onClose, 600);
    } catch (submitError) {
      setError(submitError.message || 'Could not send report.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="report-issue-backdrop" role="dialog" aria-modal="true" aria-labelledby="report-issue-title">
      <div className="report-issue-sheet">
        <div className="report-issue-header">
          <div>
            <p className="eyebrow">Beta support</p>
            <h2 id="report-issue-title">Report issue</h2>
          </div>
          <button type="button" className="btn btn-tertiary" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="report-issue-body">
          <section className="report-issue-context-summary" aria-label="Report context">
            <p className="muted-text">
              Reporting from <strong>{pageLabel}</strong>. Technical details are attached automatically.
            </p>
          </section>

          <form className="report-issue-form" onSubmit={handleSubmit}>
            <label htmlFor="global-report-category">What kind of issue is it?</label>
            <select
              id="global-report-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              disabled={isSubmitting}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            <label htmlFor="global-report-happened">What happened?</label>
            <textarea
              id="global-report-happened"
              value={whatHappened}
              onChange={(event) => setWhatHappened(event.target.value)}
              disabled={isSubmitting}
              placeholder="Tell us what happened in a sentence."
              autoFocus
            />

            <section className="report-issue-screenshot-note" aria-label="Screenshot guidance">
              <h3>Screenshots</h3>
              <p className="muted-text">
                Screenshots can help support debugging, but only share them when safe. Avoid
                passwords, tokens, sensitive personal information, raw OCR text, and raw provider
                or LLM output. Upload support is not built into this form yet.
              </p>
            </section>
            <p className="muted-text">
              If this form is not the right shape for the problem, email{' '}
              <a href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>{SUPPORT_EMAIL_ADDRESS}</a>.{' '}
              {SUPPORT_EMAIL_GUIDANCE}
            </p>
            {notice ? <p className="notice notice-info">{notice}</p> : null}
            {error ? <p role="alert" className="notice notice-error">{error}</p> : null}

            <div className="report-issue-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                {isSubmitting ? 'Sending...' : 'Send report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ReportIssueModal;
